import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import DocumentDetailModal from '../components/DocumentDetailModal';
import { generateBillOfSalePDF, generateInvoicePDF, downloadPDF } from '../utils/pdfGenerator';
import './Dashboard.css';

const Dashboard = () => {
  const { user, isPremium } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentBills, setRecentBills] = useState([]);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch stats
        const statsResponse = await api.get('/user/stats');
        setStats(statsResponse.data.stats);

        // Fetch recent documents (only for premium users)
        if (isPremium()) {
          const [billsResponse, invoicesResponse] = await Promise.all([
            api.get('/bills?limit=5'),
            api.get('/invoices?limit=5')
          ]);
          setRecentBills(billsResponse.data.bills || []);
          setRecentInvoices(invoicesResponse.data.invoices || []);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isPremium]);

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Document actions
  const handleViewDocument = (doc, type) => {
    setSelectedDocument(doc);
    setSelectedType(type);
  };

  const handleCloseModal = () => {
    setSelectedDocument(null);
    setSelectedType(null);
  };

  const handleDownloadPDF = (doc, type) => {
    try {
      if (type === 'bill') {
        const pdf = generateBillOfSalePDF(doc);
        downloadPDF(pdf, `bill-of-sale-${doc.documentNumber}.pdf`);
      } else {
        const pdf = generateInvoicePDF(doc);
        downloadPDF(pdf, `invoice-${doc.invoiceNumber}.pdf`);
      }
      toast.success('PDF downloaded!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Error generating PDF');
    }
  };

  const handleResendEmail = async (doc, type) => {
    const email = type === 'bill' 
      ? (doc.seller?.email || doc.buyer?.email)
      : (doc.from?.email || doc.to?.email);
    
    if (!email) {
      toast.error('No email addresses found for this document');
      return;
    }

    setActionLoading(true);
    try {
      const endpoint = type === 'bill' ? `/bills/${doc._id}/resend-email` : `/invoices/${doc._id}/resend-email`;
      await api.post(endpoint);
      toast.success('Email sent successfully!');
    } catch (error) {
      console.error('Error resending email:', error);
      toast.error('Error sending email');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteDocument = async (doc, type) => {
    if (!window.confirm('Are you sure you want to delete this document? This action cannot be undone.')) {
      return;
    }

    setActionLoading(true);
    try {
      const endpoint = type === 'bill' ? `/bills/${doc._id}` : `/invoices/${doc._id}`;
      await api.delete(endpoint);
      
      // Update local state
      if (type === 'bill') {
        setRecentBills(prev => prev.filter(b => b._id !== doc._id));
      } else {
        setRecentInvoices(prev => prev.filter(i => i._id !== doc._id));
      }
      
      handleCloseModal();
      toast.success('Document deleted');
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Error deleting document');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-page">
        <div className="container">
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <div className="container">
        <div className="dashboard-header">
          <div>
            <h1 className="page-title">Welcome back, {user?.firstName}!</h1>
            <p className="page-subtitle">
              {isPremium() 
                ? 'Manage your documents and track your transactions'
                : 'Generate bills of sale and invoices for your transactions'}
            </p>
          </div>
          {!isPremium() && (
            <Link to="/subscription" className="btn btn-success">
              Upgrade to Premium
            </Link>
          )}
        </div>

        {/* Stats Cards */}
        <div className="stats-grid">
          <Link to="/documents?type=bills" className="stat-card stat-card-link">
            <div className="stat-icon"></div>
            <div className="stat-content">
              <span className="stat-value">{stats?.billsOfSale || 0}</span>
              <span className="stat-label">Bills of Sale</span>
            </div>
          </Link>
          <Link to="/documents?type=invoices" className="stat-card stat-card-link">
            <div className="stat-icon">💰</div>
            <div className="stat-content">
              <span className="stat-value">{stats?.invoices || 0}</span>
              <span className="stat-label">Invoices</span>
            </div>
          </Link>
          <div className="stat-card">
            <div className="stat-icon">{isPremium() ? '⭐' : '🆓'}</div>
            <div className="stat-content">
              <span className="stat-value">
                {isPremium() 
                  ? (user?.subscription?.billingPeriod === 'yearly' ? 'Yearly Premium' : 'Monthly Premium')
                  : 'Free'
                }
              </span>
              <span className="stat-label">Current Plan</span>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="dashboard-section">
          <h2 className="section-title">Quick Actions</h2>
          <div className="quick-actions">
            <Link to="/bill-of-sale" className="action-card">
              <div className="action-icon">📝</div>
              <div className="action-content">
                <h3>Create Bill of Sale</h3>
                <p>Generate a new bill of sale document</p>
              </div>
              <span className="action-arrow">→</span>
            </Link>
            <Link to="/invoice" className="action-card">
              <div className="action-icon">💰</div>
              <div className="action-content">
                <h3>Create Invoice</h3>
                <p>Generate a new invoice document</p>
              </div>
              <span className="action-arrow">→</span>
            </Link>
          </div>
        </div>

        {/* Premium Features / Upgrade Prompt */}
        {isPremium() ? (
          <>
            {/* Recent Bills of Sale */}
            {recentBills.length > 0 && (
              <div className="dashboard-section">
                <div className="section-header">
                  <h2 className="section-title">Recent Bills of Sale</h2>
                  <Link to="/documents?type=bills" className="view-all-link">View All →</Link>
                </div>
                <div className="documents-list">
                  {recentBills.map(bill => (
                    <div key={bill._id} className="document-item">
                      <div className="document-info">
                        <h4>{bill.item?.description || 'Bill of Sale'}</h4>
                        <p>
                          {bill.documentNumber} • {formatDate(bill.createdAt)}
                        </p>
                      </div>
                      <div className="document-amount">
                        {formatCurrency(bill.transaction?.salePrice || 0)}
                      </div>
                      <span className={`badge badge-${
                        bill.sellerSignature || bill.buyerSignature ? 'success' : 'secondary'
                      }`}>
                        {bill.sellerSignature || bill.buyerSignature ? 'Signed' : 'Unsigned'}
                      </span>
                      <div className="document-actions">
                        <button 
                          className="action-btn" 
                          onClick={() => handleViewDocument(bill, 'bill')}
                          title="View Details"
                        >
                          👁️
                        </button>
                        <button 
                          className="action-btn" 
                          onClick={() => handleDownloadPDF(bill, 'bill')}
                          title="Download PDF"
                        >
                          📥
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Invoices */}
            {recentInvoices.length > 0 && (
              <div className="dashboard-section">
                <div className="section-header">
                  <h2 className="section-title">Recent Invoices</h2>
                  <Link to="/documents?type=invoices" className="view-all-link">View All →</Link>
                </div>
                <div className="documents-list">
                  {recentInvoices.map(invoice => (
                    <div key={invoice._id} className="document-item">
                      <div className="document-info">
                        <h4>{invoice.to?.name || 'Invoice'}</h4>
                        <p>
                          {invoice.invoiceNumber} • {formatDate(invoice.createdAt)}
                        </p>
                      </div>
                      <div className="document-amount">
                        {formatCurrency(invoice.total || 0)}
                      </div>
                      <span className={`badge badge-${
                        invoice.status === 'paid' ? 'success' : 
                        invoice.status === 'overdue' ? 'danger' : 'secondary'
                      }`}>
                        {invoice.status}
                      </span>
                      <div className="document-actions">
                        <button 
                          className="action-btn" 
                          onClick={() => handleViewDocument(invoice, 'invoice')}
                          title="View Details"
                        >
                          👁️
                        </button>
                        <button 
                          className="action-btn" 
                          onClick={() => handleDownloadPDF(invoice, 'invoice')}
                          title="Download PDF"
                        >
                          📥
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {recentBills.length === 0 && recentInvoices.length === 0 && (
              <div className="empty-state">
                <div className="empty-icon">📁</div>
                <h3>No documents yet</h3>
                <p>Start creating bills of sale and invoices to see them here.</p>
              </div>
            )}
          </>
        ) : (
          <div className="upgrade-prompt">
            <div className="upgrade-content">
              <h2>Unlock Document History</h2>
              <p>Upgrade to Premium to save and access all your documents anytime.</p>
              <ul className="upgrade-features">
                <li>✓ Save unlimited documents</li>
                <li>✓ Access document history</li>
                <li>✓ No ads</li>
                <li>✓ Priority support</li>
              </ul>
              <Link to="/subscription" className="btn btn-success btn-lg">
                Upgrade for $4.99/month
              </Link>
            </div>
          </div>
        )}

        {/* Subscription Info */}
        <div className="dashboard-section">
          <h2 className="section-title">Your Subscription</h2>
          <div className="subscription-info">
            <div className="subscription-plan">
              <span className="plan-name">
                {isPremium() 
                  ? `⭐ ${user?.subscription?.billingPeriod === 'yearly' ? 'Yearly' : 'Monthly'} Premium Plan`
                  : '🆓 Free Plan'
                }
              </span>
              {isPremium() && user?.subscription?.endDate && (
                <span className="plan-expires">
                  Renews on {formatDate(user.subscription.endDate)}
                </span>
              )}
            </div>
            <Link to="/subscription" className="btn btn-secondary">
              {isPremium() ? 'Manage Subscription' : 'View Plans'}
            </Link>
          </div>
        </div>
      </div>

      {/* Document Detail Modal */}
      {selectedDocument && (
        <DocumentDetailModal
          document={selectedDocument}
          type={selectedType}
          onClose={handleCloseModal}
          onDownload={() => handleDownloadPDF(selectedDocument, selectedType)}
          onResendEmail={() => handleResendEmail(selectedDocument, selectedType)}
          onDelete={() => handleDeleteDocument(selectedDocument, selectedType)}
          loading={actionLoading}
        />
      )}
    </div>
  );
};

export default Dashboard;
